import React from "react";
import { useTranslation } from "react-i18next";
import { HailingStepData } from "../mock/data";
import {
  MeLocationService,
  PickupPointOption,
  PlaceSuggestion,
  ResolvedPlace,
} from "../../../api/meLocation.service";
import { usePlaceSearch } from "../hooks/usePlaceSearch";
import { useRecentPlaces, RecentPlaceEntry } from "../hooks/useRecentPlaces";
import { HailingQuoteResponse } from "../../../api/meHailingQuote.service";
import { getPickupTime } from "../tracker/tripStatus.helper";
import PlacePointPicker from "./where/PlacePointPicker";
import PlaceResults from "./where/PlaceResults";
import RecentPlaces from "./where/RecentPlaces";
import {
  SCHEDULE_MIN_LEAD_MINUTES,
  defaultScheduledTime,
  earliestScheduleInputValue,
  fromLocalInputValue,
  isScheduleTooSoon,
  toLocalInputValue,
} from "../schedule";

export interface WhereToProps {
  state: HailingStepData;
  setState: React.Dispatch<React.SetStateAction<HailingStepData>>;
  onStepComplete: () => void | Promise<void>;
  signedIn: boolean;
  quote?: HailingQuoteResponse | undefined;
  quoteStatus: "idle" | "loading" | "success" | "error";
  submittingPrices: boolean;
}

// "Where to" screen — mirrors the mockup's booking-panel where/route sections
// (Web Hailing.dc.html). Search panel: "Get a taxi" title + "Now" pill, gray
// pickup/drop-off group with BOTH fields editable, live search results and
// Recent list — no CTA. Once a place is picked for both fields the route
// panel shows instead: white address card with swap, trip stats, Add a stop,
// and the "See Prices" footer (the sign-in gate).
type Field = "pickup" | "dropoff";

// Hidden for now: quotes and orders accept exactly two places (pickup and
// drop-off), so a stop is rejected with "placeIds: expected exactly 2 items".
const ADD_STOP_ENABLED = false;

const WhereTo: React.FC<WhereToProps> = ({
  state,
  setState,
  onStepComplete,
  signedIn,
  quote,
  quoteStatus,
  submittingPrices,
}) => {
  const { t } = useTranslation("hailing");
  // Editable field values — both start empty until a place is picked (the pickup
  // can also be filled from the device location)
  const [pickupQ, setPickupQ] = React.useState(state.pickupPlace);
  const [dropoffQ, setDropoffQ] = React.useState(state.dropoffPlace);
  // Which input the search dropdown / Enter key targets (mockup focuses the
  // drop-off field by default)
  const [activeField, setActiveField] = React.useState<Field>("dropoff");
  // True after picking a place — hides the dropdown
  const [picked, setPicked] = React.useState(false);
  // Mockup "panel.route": false = search panel, true = route panel. The route
  // panel (address card + See Prices) appears once BOTH fields have a value.
  const [showRoute, setShowRoute] = React.useState(false);
  // Intermediate (3rd) stop picked from "Add a stop". Shown as a separate
  // row in the route card; it also participates in the mockup's fare estimate.
  const [stopQ, setStopQ] = React.useState("");
  // True after picking an intermediate stop
  const [stopPicked, setStopPicked] = React.useState(false);
  // Whether the intermediate stop editor is open (searchable row in route card)
  const [stopEditing, setStopEditing] = React.useState(false);
  // "Now" books a live ride; "Schedule" books one for state.scheduledTime.
  const scheduleNow = state.scheduledTime === null;
  const scheduleTooSoon = isScheduleTooSoon(state.scheduledTime);
  // Whether the schedule dropdown panel is expanded
  const [showSchedule, setShowSchedule] = React.useState(false);
  const scheduleControlRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!showSchedule) return;
    const onPointerDown = (event: MouseEvent) => {
      if (
        event.target instanceof Node &&
        !scheduleControlRef.current?.contains(event.target)
      ) {
        setShowSchedule(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowSchedule(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showSchedule]);

  const pickupRef = React.useRef<HTMLInputElement>(null);
  const dropoffRef = React.useRef<HTMLInputElement>(null);

  // Which field to focus after returning from the route panel ("Edit")
  const [focusTarget, setFocusTarget] = React.useState<"pickup" | "dropoff" | null>(null);

  React.useEffect(() => {
    if (focusTarget === "pickup") pickupRef.current?.focus();
    if (focusTarget === "dropoff") dropoffRef.current?.focus();
    if (focusTarget) setFocusTarget(null);
  }, [focusTarget]);

  const activeQuery = activeField === "pickup" ? pickupQ : dropoffQ;
  const trimmed = activeQuery.trim();

  // Live place search (v3 place-autocomplete, debounced in the hook) for the
  // active field: pickup is itinerary index 0, drop-off is index 1. The stop
  // field is part of the drop-off leg, so it shares index 1.
  const activeIndex: 0 | 1 = activeField === "pickup" ? 0 : 1;
  const activeLocationBias =
    activeField !== "pickup" && state.pickupLat != null && state.pickupLng != null
      ? { lat: state.pickupLat, lng: state.pickupLng }
      : {};
  const stopLocationBias =
    state.pickupLat != null && state.pickupLng != null
      ? { lat: state.pickupLat, lng: state.pickupLng }
      : {};
  const {
    suggestions: liveResults,
    searching,
    searchError,
  } = usePlaceSearch({
    query: activeQuery,
    index: activeIndex,
    signedIn,
    ...activeLocationBias,
  });
  const {
    suggestions: liveStopResults,
    searching: stopSearching,
    searchError: stopSearchError,
  } = usePlaceSearch({
    query: stopQ,
    index: 1,
    signedIn,
    ...stopLocationBias,
    enabled: stopEditing,
  });
  const results: PlaceSuggestion[] = liveResults;
  const stopResults: PlaceSuggestion[] = liveStopResults;
  const tunnelFees = quote?.tunnelFees;
  const tunnelFeeTotal = tunnelFees?.reduce(
    (total, item) =>
      total + (typeof item.fee === "number" && Number.isFinite(item.fee) ? item.fee : 0),
    0,
  );

  // Rider-picked recents (localStorage).
  const { recents, pushRecent } = useRecentPlaces();

  // Resolving a pick via place-details (spinner on the tapped row).
  const [resolvingId, setResolvingId] = React.useState<string | null>(null);
  const [resolveError, setResolveError] = React.useState(false);
  const [resolvingStop, setResolvingStop] = React.useState(false);
  const [stopResolveError, setStopResolveError] = React.useState(false);

  // "Use current location" reverse-geocode state.
  const [locating, setLocating] = React.useState(false);
  const [locateError, setLocateError] = React.useState(false);

  // A place with several official points (a mall, an airport) is not used as it is. For the
  // pick-up the rider must choose a point; for the drop-off a point or the place's main address.
  const [pointChoice, setPointChoice] = React.useState<{
    field: Field;
    place: ResolvedPlace;
    points: PickupPointOption[];
  } | null>(null);
  const [resolvingPointId, setResolvingPointId] = React.useState<string | null>(null);
  const [pointError, setPointError] = React.useState(false);
  const closePointChoice = (field: Field) =>
    setPointChoice((choice) => (choice?.field === field ? null : choice));

  const selectStop = (r: PlaceSuggestion) => {
    setResolvingStop(true);
    setStopResolveError(false);
    new MeLocationService()
      .placeDetails([r.placeId])
      .then((resolved) => {
        setResolvingStop(false);
        if (!resolved) {
          setStopResolveError(true);
          return;
        }
        setStopQ(resolved.displayName);
        setStopPicked(true);
        setState((prev) => ({
          ...prev,
          stopPlace: resolved.displayName,
          stopAddress: resolved.formattedAddress,
          stopPlaceId: resolved.placeId,
          stopLat: resolved.lat,
          stopLng: resolved.lng,
        }));
        pushRecent(resolved);
      })
      .catch(() => {
        setResolvingStop(false);
        setStopResolveError(true);
      });
  };

  const clearStop = () => {
    setStopQ("");
    setStopPicked(false);
    setState((prev) => ({
      ...prev,
      stopPlace: "",
      stopAddress: "",
      stopPlaceId: "",
      stopLat: null,
      stopLng: null,
    }));
  };

  // Choose an intermediate stop (or clear the stop).
  // "Add a stop" becomes a searchable 3rd-stop row in the route card.
  const toggleStop = (e: React.MouseEvent) => {
    e.preventDefault();
    if (stopEditing) {
      // Close the stop editor and clear any partial query
      setStopEditing(false);
      clearStop();
    } else {
      // Open the stop editor so the user can search for a stop
      setStopEditing(true);
      clearStop();
    }
  };

  // Commit a picked place into the active field (pickup / drop-off). Picking
  // Apply a resolved place to a field: text + placeId/coords (the quote path
  // needs real placeIds, not display strings).
  const setField = (
    field: "pickup" | "dropoff",
    place: ResolvedPlace,
    options: { skipPoints?: boolean } = {},
  ) => {
    if (!options.skipPoints) {
      const points = place.pickupPoints ?? [];
      // A place that is itself one of its points (e.g. a recent pick) needs no choice.
      const isPoint = points.some((point) => point.placeId === place.placeId);
      if (points.length > 1 && !isPoint) {
        if (field === "pickup") {
          setPickupQ(place.displayName);
          setState((prev) => ({
            ...prev,
            pickupPlace: "",
            pickupAddress: "",
            pickupPlaceId: "",
            pickupLat: null,
            pickupLng: null,
          }));
        } else {
          setDropoffQ(place.displayName);
          setState((prev) => ({
            ...prev,
            dropoffPlace: "",
            dropoffAddress: "",
            dropoffPlaceId: "",
            dropoffLat: null,
            dropoffLng: null,
          }));
        }
        setPointChoice({ field, place, points });
        setPointError(false);
        setPicked(true);
        setShowRoute(false);
        return;
      }
      // A single pick-up point is used as the pick-up; a drop-off keeps its main address.
      if (field === "pickup" && points.length === 1 && !isPoint) {
        choosePoint("pickup", points[0]!, place.displayName);
        return;
      }
    }
    closePointChoice(field);
    if (field === "pickup") {
      setPickupQ(place.displayName);
      setState((prev) => ({
        ...prev,
        pickupPlace: place.displayName,
        pickupAddress: place.formattedAddress,
        pickupPlaceId: place.placeId,
        pickupLat: place.lat,
        pickupLng: place.lng,
      }));
    } else {
      setDropoffQ(place.displayName);
      setState((prev) => ({
        ...prev,
        dropoffPlace: place.displayName,
        dropoffAddress: place.formattedAddress,
        dropoffPlaceId: place.placeId,
        dropoffLat: place.lat,
        dropoffLng: place.lng,
      }));
    }
    pushRecent({
      placeId: place.placeId,
      displayName: place.displayName,
      formattedAddress: place.formattedAddress,
      lat: place.lat,
      lng: place.lng,
    });
    setPicked(true);
    setShowRoute(
      field === "pickup"
        ? Boolean(place.placeId && state.dropoffPlaceId)
        : Boolean(state.pickupPlaceId && place.placeId)
    );
  };

  // The chosen point becomes the pick-up or drop-off. A point without coordinates is resolved
  // first.
  const choosePoint = (field: Field, point: PickupPointOption, placeName: string) => {
    const label = `${placeName} · ${point.displayName}`;
    const commit = (resolved: ResolvedPlace) =>
      setField(
        field,
        {
          placeId: point.placeId,
          displayName: label,
          formattedAddress: point.formattedAddress || resolved.formattedAddress,
          lat: resolved.lat,
          lng: resolved.lng,
        },
        { skipPoints: true },
      );
    if (point.lat != null && point.lng != null) {
      commit(point);
      return;
    }
    setResolvingPointId(point.placeId);
    setPointError(false);
    new MeLocationService()
      .placeDetails([point.placeId])
      .then((resolved) => {
        setResolvingPointId(null);
        if (!resolved || resolved.lat == null || resolved.lng == null) {
          setPointError(true);
          return;
        }
        commit(resolved);
      })
      .catch(() => {
        setResolvingPointId(null);
        setPointError(true);
      });
  };

  const selectResult = (r: PlaceSuggestion) => {
    setResolvingId(r.placeId);
    setResolveError(false);
    new MeLocationService()
      .placeDetails([r.placeId])
      .then((resolved) => {
        setResolvingId(null);
        if (!resolved) {
          setResolveError(true);
          return;
        }
        setField(activeField, resolved);
      })
      .catch(() => {
        setResolvingId(null);
        setResolveError(true);
      });
  };

  // A recent place fills whichever itinerary field is active. Recents already
  // hold real placeIds and resolved coordinates. A recent place is looked up again for its
  // points, which recents do not keep; it is used as stored if that fails.
  const selectRecent = (r: RecentPlaceEntry) => {
    const field = activeField;
    if (field === "pickup") setPickupQ(r.displayName);
    else setDropoffQ(r.displayName);
    setResolvingId(r.placeId);
    new MeLocationService()
      .placeDetails([r.placeId])
      .then((resolved) => {
        setResolvingId(null);
        setField(field, resolved?.pickupPoints ? { ...r, pickupPoints: resolved.pickupPoints } : r);
      })
      .catch(() => {
        setResolvingId(null);
        setField(field, r);
      });
  };

  // Intermediate stop dropdown (hidden until "Add a stop" is tapped)
  const renderStopDropdown = () => {
    if (!stopQ.trim() || stopPicked) return null;
    return (
      <div className="mt-2 overflow-hidden rounded-2xl bg-white p-1.5 shadow-[0_8px_24px_rgba(15,21,26,0.1)]">
        {stopSearching ? (
          <div className="px-2.5 py-3 text-[13px] text-[#6B7075]">
            {t("steps.where.searching")}
          </div>
        ) : stopResults.length > 0 ? (
          stopResults.map((r) => (
            <button
              key={r.placeId}
              type="button"
              onMouseDown={keepFocus}
              onClick={() => selectStop(r)}
              className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left hover:bg-[#F2F3F3] focus-visible:bg-[#F2F3F3] focus-visible:outline-none"
            >
              <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#ECECEC]">
                <span className="font-material-symbols text-[18px] leading-none text-ink-100">
                  location_on
                </span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] text-[#3A4045]">
                  {highlight(r.mainText)}
                </span>
                {r.secondaryText ? (
                  <span className="mt-0.5 block truncate text-[12px] text-[#6B7075]">
                    {r.secondaryText}
                  </span>
                ) : null}
              </span>
            </button>
          ))
        ) : (
          <div className="px-2.5 py-3 text-[13px] text-[#6B7075]">
            {stopResolveError
              ? t("steps.where.resolveError")
              : stopSearchError
                ? t("steps.where.searchError")
                : !signedIn && stopQ.trim()
                  ? t("steps.where.signInToSearch")
                  : t("steps.where.noResults")}
          </div>
        )}
        {resolvingStop && (
          <div className="px-2.5 py-3 text-[13px] text-[#6B7075]">
            {t("steps.where.searching")}
          </div>
        )}
      </div>
    );
  };

  const clearField = (field: Field) => {
    closePointChoice(field);
    if (field === "pickup") {
      setPickupQ("");
      setState((prev) => ({ ...prev, pickupPlace: "", pickupAddress: "", pickupPlaceId: "", pickupLat: null, pickupLng: null }));
    } else {
      setDropoffQ("");
      setState((prev) => ({ ...prev, dropoffPlace: "", dropoffAddress: "", dropoffPlaceId: "", dropoffLat: null, dropoffLng: null }));
    }
    setPicked(true);
    setShowRoute(false);
  };

  const editField = (field: Field, value: string) => {
    setPicked(false);
    setShowRoute(false);
    closePointChoice(field);
    if (field === "pickup") {
      setPickupQ(value);
      setState((prev) => ({
        ...prev,
        pickupPlace: "",
        pickupAddress: "",
        pickupPlaceId: "",
        pickupLat: null,
        pickupLng: null,
      }));
    } else {
      setDropoffQ(value);
      setState((prev) => ({
        ...prev,
        dropoffPlace: "",
        dropoffAddress: "",
        dropoffPlaceId: "",
        dropoffLat: null,
        dropoffLng: null,
      }));
    }
  };

  const editStop = (value: string) => {
    setStopQ(value);
    setStopPicked(false);
    setState((prev) => ({
      ...prev,
      stopPlace: "",
      stopAddress: "",
      stopPlaceId: "",
      stopLat: null,
      stopLng: null,
    }));
  };

  // "Use my location" (blue my_location icon on the pickup row) — reverse-geocode
  // the device coords into a real place (tester: reverse-geocode + place-details).
  const useMyLocation = () => {
    if (!signedIn) return;
    if (!navigator.geolocation) {
      setLocateError(true);
      return;
    }
    setLocating(true);
    setLocateError(false);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        new MeLocationService()
          .reverseGeocode({ lat: pos.coords.latitude, lng: pos.coords.longitude, index: 0 })
          .then((resolved) => {
            setLocating(false);
            if (!resolved) {
              setLocateError(true);
              return;
            }
            setField("pickup", resolved);
          })
          .catch(() => {
            setLocating(false);
            setLocateError(true);
          });
      },
      () => {
        setLocating(false);
        setLocateError(true);
      },
      { timeout: 10000 },
    );
  };

  // Swap pickup and drop-off (route card button) — swaps placeIds/coords too.
  const swapFields = () => {
    setPickupQ(dropoffQ);
    setDropoffQ(pickupQ);
    setState((prev) => ({
      ...prev,
      pickupPlace: prev.dropoffPlace,
      pickupAddress: prev.dropoffAddress,
      pickupPlaceId: prev.dropoffPlaceId,
      pickupLat: prev.dropoffLat,
      pickupLng: prev.dropoffLng,
      dropoffPlace: prev.pickupPlace,
      dropoffAddress: prev.pickupAddress,
      dropoffPlaceId: prev.pickupPlaceId,
      dropoffLat: prev.pickupLat,
      dropoffLng: prev.pickupLng,
    }));
    // Each swapped place may now need a point chosen for its new role. The pick-up goes first;
    // only one choice shows at a time, so the drop-off is checked once the pick-up needs none.
    // Best effort: a failed lookup leaves the swapped place as it is.
    const lookUpPoints = (placeId: string): Promise<PickupPointOption[] | undefined> =>
      placeId
        ? new MeLocationService()
            .placeDetails([placeId])
            .then((resolved) => resolved?.pickupPoints)
            .catch(() => undefined)
        : Promise.resolve(undefined);
    const newPickup: ResolvedPlace = {
      placeId: state.dropoffPlaceId,
      displayName: dropoffQ,
      formattedAddress: state.dropoffAddress,
      lat: state.dropoffLat,
      lng: state.dropoffLng,
    };
    const newDropoff: ResolvedPlace = {
      placeId: state.pickupPlaceId,
      displayName: pickupQ,
      formattedAddress: state.pickupAddress,
      lat: state.pickupLat,
      lng: state.pickupLng,
    };
    void lookUpPoints(newPickup.placeId).then((points) => {
      if (points) setField("pickup", { ...newPickup, pickupPoints: points });
      const pickupNeedsChoice =
        (points?.length ?? 0) > 1 && !points?.some((point) => point.placeId === newPickup.placeId);
      if (pickupNeedsChoice) return undefined;
      return lookUpPoints(newDropoff.placeId).then((dropoffPoints) => {
        if (dropoffPoints) setField("dropoff", { ...newDropoff, pickupPoints: dropoffPoints });
      });
    });
  };

  // Bold exactly the typed substring inside the result name (mockup shows
  // the typed part bold + the remainder regular)
  const highlight = (name: string) => {
    if (!trimmed) return name;
    const idx = name.toLowerCase().indexOf(trimmed.toLowerCase());
    if (idx < 0) return name;
    return (
      <>
        {name.slice(0, idx)}
        <span className="font-semibold text-ink-100">
          {name.slice(idx, idx + trimmed.length)}
        </span>
        {name.slice(idx + trimmed.length)}
      </>
    );
  };

  // "Now" schedule pill - toggles the schedule dropdown picker
  const toggleSchedule = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowSchedule((prev) => !prev);
  };

  // Choose "Now" (immediate) or a scheduled time
  const chooseSchedule = (now: boolean) => {
    setState((prev) => ({
      ...prev,
      scheduledTime: now ? null : prev.scheduledTime ?? defaultScheduledTime(),
    }));
    setShowSchedule(false);
    setPicked(true);
    if (state.pickupPlaceId && state.dropoffPlaceId) setShowRoute(true);
  };

  const changeScheduledTime = (value: string) => {
    const scheduledTime = fromLocalInputValue(value);
    if (scheduledTime) setState((prev) => ({ ...prev, scheduledTime }));
  };

  const scheduleLabel = scheduleNow
    ? t("steps.where.now")
    : getPickupTime(t, state.scheduledTime);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && results.length > 0) {
      selectResult(results[0]!);
    }
  };

  // Hides the dropdown on a genuine blur. Moving focus to the *other* input
  // is not a commit; leaving the search panel with both fields filled returns
  // to the route panel (the values are kept as typed/committed).
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.relatedTarget as HTMLElement | null;
    if (next?.dataset.whereField) return;
    setPicked(true);
    if (state.pickupPlaceId && state.dropoffPlaceId) setShowRoute(true);
  };

  // Keep focus on the input when clicking dropdown items / row buttons so a
  // click doesn't blur (and prematurely leave) mid-selection
  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className="flex flex-1 flex-col">
      {/* Title row: "Get a taxi" + "Now" time pill */}
      <div className="flex items-center justify-between">
        <h1 className="text-[30px] font-bold tracking-[-0.6px] text-ink-100">
          {t("steps.where.title")}
        </h1>
        <div ref={scheduleControlRef} className="relative z-10 shrink-0">
          <button
            type="button"
            onClick={toggleSchedule}
            aria-label={scheduleLabel}
            aria-expanded={showSchedule}
            aria-haspopup="menu"
            aria-controls="hailing-schedule-menu"
            className="flex h-[40px] min-w-[104px] items-center justify-center gap-1 whitespace-nowrap rounded-full border-[1.5px] border-brand-blue bg-white pl-3.5 pr-2.5 text-[14px] font-medium text-brand-blue transition-colors hover:bg-[#F2F8FD] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
          >
            {scheduleLabel}
            <span className="font-material-symbols text-[20px] leading-none">
              expand_more
            </span>
          </button>

          {showSchedule && (
            <div
              id="hailing-schedule-menu"
              role="menu"
              aria-label={t("steps.where.schedule")}
              className="absolute right-0 top-[calc(100%+8px)] z-30 w-48 overflow-hidden rounded-2xl border border-[#E3E4E5] bg-white p-1.5 shadow-[0_8px_24px_rgba(15,21,26,0.14)]"
            >
              <button
                type="button"
                role="menuitemradio"
                aria-checked={scheduleNow}
                onClick={() => chooseSchedule(true)}
                className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] text-ink-100 transition-colors hover:bg-[#F2F3F3] focus-visible:bg-[#F2F3F3] focus-visible:outline-none"
              >
                <span className="font-material-symbols text-[20px] leading-none text-brand-blue">
                  bolt
                </span>
                <span className="flex-1">{t("steps.where.now")}</span>
                {scheduleNow && (
                  <span
                    aria-hidden="true"
                    className="font-material-symbols text-[18px] leading-none text-brand-blue"
                  >
                    check
                  </span>
                )}
              </button>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={!scheduleNow}
                onClick={() => chooseSchedule(false)}
                className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] text-ink-100 transition-colors hover:bg-[#F2F3F3] focus-visible:bg-[#F2F3F3] focus-visible:outline-none"
              >
                <span className="font-material-symbols text-[20px] leading-none text-brand-blue">
                  schedule
                </span>
                <span className="flex-1">{t("steps.where.schedule")}</span>
                {!scheduleNow && (
                  <span
                    aria-hidden="true"
                    className="font-material-symbols text-[18px] leading-none text-brand-blue"
                  >
                    check
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pickup time of a scheduled ride: at least SCHEDULE_MIN_LEAD_MINUTES ahead,
          as the quote API requires. */}
      {!scheduleNow && state.scheduledTime && (
        <div className="mt-4">
          <label
            htmlFor="hailing-pickup-time"
            className="text-[12px] font-semibold text-[#6B7075]"
          >
            {t("steps.where.pickupTime")}
          </label>
          <input
            id="hailing-pickup-time"
            type="datetime-local"
            value={toLocalInputValue(state.scheduledTime)}
            min={earliestScheduleInputValue()}
            onChange={(e) => changeScheduledTime(e.target.value)}
            aria-invalid={scheduleTooSoon}
            aria-describedby={scheduleTooSoon ? "hailing-pickup-time-error" : undefined}
            className="mt-1.5 h-12 w-full rounded-2xl bg-white px-4 text-[15px] text-ink-100 shadow-[0_2px_8px_rgba(15,21,26,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
          />
          {scheduleTooSoon && (
            <p
              id="hailing-pickup-time-error"
              role="alert"
              className="mt-1.5 text-[12px] text-[#C62828]"
            >
              {t("steps.where.scheduleTooSoon", {
                minutes: SCHEDULE_MIN_LEAD_MINUTES,
              })}
            </p>
          )}
        </div>
      )}

      {showRoute ? (
        <>
          {/* Route panel (mockup "panel.route"): white pickup/drop-off card
              with swap, trip stats, Add a stop — plus the See Prices footer.
              Shown once both fields have a value; tap a row to edit again. */}
          <div className="relative mt-5 rounded-[18px] bg-white px-4 py-1">
            {/* Pickup row */}
            <button
              type="button"
              onClick={() => {
                setPicked(true);
                setShowRoute(false);
                setFocusTarget("pickup");
              }}
              className="flex h-[58px] w-full items-center gap-3.5 border-b border-[#E3E4E5] text-left"
            >
              <span className="box-border h-3.5 w-3.5 flex-none rounded-full border-4 border-ink-100" />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[15px] font-medium text-ink-100">
                  {pickupQ}
                </span>
                {state.pickupAddress && (
                  <span className="truncate text-[12px] text-[#6B7075]">
                    {state.pickupAddress}
                  </span>
                )}
              </span>
            </button>

            {/* Drop-off row */}
            <button
              type="button"
              onClick={() => {
                setPicked(true);
                setShowRoute(false);
                setFocusTarget("dropoff");
              }}
              className="flex h-[58px] w-full items-center gap-3.5 text-left"
            >
              <span className="flex h-3.5 w-3.5 flex-none items-center justify-center rounded-[4px] bg-ink-100">
                <span className="h-[5px] w-[5px] rounded-[1px] bg-white" />
              </span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[15px] font-medium text-ink-100">
                  {dropoffQ}
                </span>
                {state.dropoffAddress && (
                  <span className="truncate text-[12px] text-[#6B7075]">
                    {state.dropoffAddress}
                  </span>
                )}
              </span>
            </button>

            {/* Swap pickup / drop-off (mockup: right side, on the divider) */}
            <button
              type="button"
              onClick={swapFields}
              aria-label={t("steps.where.swap")}
              className="absolute right-3.5 top-[42px] flex h-[34px] w-[34px] items-center justify-center rounded-full bg-[#F2F3F3] transition-colors hover:bg-[#E4E6E6]"
            >
              <span className="font-material-symbols text-[20px] leading-none">
                swap_vert
              </span>
            </button>
          </div>

          {(quoteStatus === "loading" ||
            quoteStatus === "error" ||
            quoteStatus === "success") && (
            <div
              className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-[12px] text-[#6B7075]"
              aria-live="polite"
            >
              {quoteStatus === "loading" && (
                <span>{t("steps.where.route.calculating")}</span>
              )}
              {quoteStatus === "error" && (
                <span className="text-danger-ink">
                  {t("steps.where.route.error")}
                </span>
              )}
              {quoteStatus === "success" && quote?.distanceMeters != null && quote.distanceMeters > 0 && (
                <span>
                  {t("steps.where.route.distance", {
                    distance: (quote.distanceMeters / 1000).toFixed(1),
                  })}
                </span>
              )}
              {quoteStatus === "success" && quote?.durationSeconds != null && quote.durationSeconds > 0 && (
                <span>
                  {t("steps.where.route.duration", {
                    duration: Math.round(quote.durationSeconds / 60),
                  })}
                </span>
              )}
              {quoteStatus === "success" && tunnelFees && (
                <span>
                  {t(
                    tunnelFees.length > 0 && (tunnelFeeTotal ?? 0) > 0
                      ? "steps.where.route.tunnelFees"
                      : "steps.where.route.noTunnelFees",
                    {
                      amount: new Intl.NumberFormat("en-HK", {
                        style: "currency",
                        currency: "HKD",
                        maximumFractionDigits: 0,
                      }).format(tunnelFeeTotal ?? 0),
                    },
                  )}
                </span>
              )}
            </div>
          )}

          {/* Add a stop (mockup link row) */}
          {ADD_STOP_ENABLED && (
            <button
              type="button"
              onClick={toggleStop}
              className="mt-3.5 flex items-center gap-2 px-1 text-[13px] text-[#3A4045] transition-opacity hover:opacity-80"
            >
              <span className="font-material-symbols text-[18px] leading-none text-brand-blue">
                add_circle
              </span>
              {t("steps.where.addStop")}
            </button>
          )}

          {/* Intermediate stop search (appears after tapping "Add a stop") */}
          {ADD_STOP_ENABLED && stopEditing && (
            <div className="mt-3">
              <div className="flex items-center gap-3 rounded-2xl bg-white px-4 py-2.5 shadow-[0_2px_8px_rgba(15,21,26,0.08)]">
                <span className="font-material-symbols flex-none text-[20px] leading-none text-brand-blue">
                  location_on
                </span>
                <input
                  type="text"
                  value={stopQ}
                  onChange={(e) => editStop(e.target.value)}
                  placeholder={t("steps.where.stopPlaceholder")}
                  className="min-w-0 flex-1 bg-transparent text-[14px] text-ink-100 placeholder:text-[#8A8F93] focus:outline-none"
                />
                {stopQ && (
                  <button
                    type="button"
                    onClick={clearStop}
                    aria-label={t("steps.where.clearStop")}
                    className="font-material-symbols flex-none text-[20px] leading-none text-[#6B7075]"
                  >
                    close
                  </button>
                )}
              </div>
              {renderStopDropdown()}
            </div>
          )}

          {/* Footer: See Prices CTA + sign-in gate note
              (mockup route panel; caption only when unsigned out) */}
          <div className="mt-auto flex flex-col gap-2.5 pt-6">
            <button
              type="button"
              disabled={submittingPrices || scheduleTooSoon}
              onClick={() => {
                void onStepComplete();
              }}
              className="h-14 rounded-2xl bg-brand-blue text-[17px] font-semibold text-white transition-colors hover:bg-[#0F66B4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70"
              aria-label={t("steps.where.seePrices")}
              aria-live="polite"
            >
              {submittingPrices
                ? t("steps.where.refreshingPrices")
                : t("steps.where.seePrices")}
            </button>
            {!signedIn && (
              <p className="text-center text-[12px] text-[#6B7075]">
                {t("steps.where.signInNote")}
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Pickup + drop-off group (gray rounded container). Both fields are
              editable. No See Prices CTA yet — it appears on the route panel once both
              fields have a value (mockup: where panel has no footer). */}
          <div className="relative mt-5 rounded-[18px] bg-[#ECECEC]">
            {/* Pickup — editable; it can also be filled from device location. */}
            <div className="flex h-[54px] items-center gap-3.5 px-4">
              <span className="box-border h-3.5 w-3.5 shrink-0 rounded-full border-4 border-[#6B7075]" />
              <input
                ref={pickupRef}
                type="text"
                data-where-field="pickup"
                value={pickupQ}
                onChange={(e) => editField("pickup", e.target.value)}
                onFocus={() => setActiveField("pickup")}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={t("steps.where.pickupPlaceholder")}
                aria-label={t("steps.where.pickupPlaceholder")}
                className="min-w-0 flex-1 bg-transparent text-[15px] text-ink-100 caret-[#1479C7] outline-none placeholder:text-[#9AA0A4]"
              />
              {activeField === "pickup" && trimmed && (
                <button
                  type="button"
                  onMouseDown={keepFocus}
                  onClick={() => clearField("pickup")}
                  aria-label={t("steps.where.clearPickup")}
                  className="shrink-0"
                >
                  <span className="font-material-symbols text-[20px] leading-none text-[#B5B8BA] [font-variation-settings:'FILL'_1]">
                    cancel
                  </span>
                </button>
              )}
              {/* my_location: resolve the device location into a place */}
              <button
                type="button"
                onMouseDown={keepFocus}
                onClick={useMyLocation}
                disabled={!signedIn || locating}
                aria-label={t("steps.where.useMyLocation")}
                title={!signedIn ? t("steps.where.signInToSearch") : undefined}
                className="shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="font-material-symbols text-[20px] leading-none text-brand-blue">
                  my_location
                </span>
              </button>
            </div>

            {/* Drop-off: active white field with blue gradient border */}
            <div
              className="mt-2 flex h-[54px] items-center gap-3 rounded-[18px] border-2 border-transparent px-3.5"
              style={{
                background:
                  "linear-gradient(#fff,#fff) padding-box, linear-gradient(90deg,#56A9FC,#1479C7) border-box",
              }}
            >
              <span className="font-material-symbols shrink-0 text-[22px] leading-none text-brand-blue [font-variation-settings:'FILL'_1]">
                location_on
              </span>
              <input
                ref={dropoffRef}
                type="text"
                data-where-field="dropoff"
                value={dropoffQ}
                onChange={(e) => editField("dropoff", e.target.value)}
                onFocus={() => setActiveField("dropoff")}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={t("steps.where.placeholder")}
                aria-label={t("steps.where.placeholder")}
                className="min-w-0 flex-1 bg-transparent text-[15px] text-ink-100 caret-[#1479C7] outline-none placeholder:text-[#9AA0A4]"
              />
              {activeField === "dropoff" && trimmed && (
                <button
                  type="button"
                  onMouseDown={keepFocus}
                  onClick={() => clearField("dropoff")}
                  aria-label={t("steps.where.clear")}
                  className="shrink-0"
                >
                  <span className="font-material-symbols text-[20px] leading-none text-[#B5B8BA] [font-variation-settings:'FILL'_1]">
                    cancel
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Pick-up points: the rider must choose one before the route and prices show. */}
          {pointChoice && (
            <PlacePointPicker
              field={pointChoice.field}
              placeName={pointChoice.place.displayName}
              points={pointChoice.points}
              resolvingPointId={resolvingPointId}
              error={pointError}
              onChoose={(point) =>
                choosePoint(pointChoice.field, point, pointChoice.place.displayName)
              }
              {...(pointChoice.field === "dropoff"
                ? {
                    onUseMainAddress: () =>
                      setField("dropoff", pointChoice.place, { skipPoints: true }),
                  }
                : {})}
              onMouseDown={keepFocus}
            />
          )}

          {/* Live search results dropdown (for the active field; hidden once
              a place is picked) */}
          {trimmed && !picked && (
            <PlaceResults
              query={trimmed}
              results={results}
              searching={searching}
              searchError={searchError}
              signedIn={signedIn}
              resolvingId={resolvingId}
              resolveError={resolveError}
              locateError={locateError}
              onSelect={selectResult}
              onMouseDown={keepFocus}
              highlight={highlight}
            />
          )}
          {locating && (
            <div className="mt-2 px-2.5 py-3 text-[13px] text-[#6B7075]">
              {t("steps.where.locating")}
            </div>
          )}

          {/* Recent places fill whichever location field is active. */}
          {recents.length > 0 && (
            <RecentPlaces recents={recents} onSelect={selectRecent} onMouseDown={keepFocus} />
          )}
        </>
      )}
    </div>
  );
};

export default WhereTo;
